<template>
    <div
        class="flex flex-col items-center mx-auto w-full max-w-[19.5rem] large:my-auto large:pb-[7.6875rem] large:max-w-[26.25rem]">
        <h1 v-if="$root.config.emailOptIn.content.heading" class="heading-s mb-3 font-medium text-center xLarge:mb-4">
            {{ $root.config.emailOptIn.content.heading }}
        </h1>
        <div v-if="$root.config.emailOptIn.content.description" class="wysiwyg body-s text-center"
            v-html="descriptionHtml" />
        <div v-if="$root.config.emailOptIn.content.formHtml" class="mt-6 w-full xLarge:mt-[3.125rem]"
            v-html="formHtml" />
        <div v-if="$root.config.emailOptIn.canSkip" class="mt-4 xLarge:mt-5">
            <button type="button" class="body-xs underline" @click="goToNext">
                {{ $root.config.translations.emailOptIn.skipThisStep }}
            </button>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            klaviyoLoaded: false,
            form: null,
            isSubmitting: false,
        }
    },
    computed: {
        descriptionHtml() {
            // Unescapes HTML entities
            const tempEl = document.createElement('textarea');
            tempEl.innerHTML = this.$root.config.emailOptIn.content.description;

            // Parses as DOM so values can be changed
            const parser = new DOMParser();
            let htmlBody = parser.parseFromString(tempEl.value, 'text/html');

            // Returns modified parsed HTML
            return htmlBody.body.innerHTML;
        },
        formHtml() {
            // Unescapes HTML entities
            const tempEl = document.createElement('textarea');
            tempEl.innerHTML = this.$root.config.emailOptIn.content.formHtml;

            // Parses as DOM so values can be changed
            const parser = new DOMParser();
            let htmlBody = parser.parseFromString(tempEl.value, 'text/html');

            // Returns modified parsed HTML
            return htmlBody.body.innerHTML;
        }
    },
    mounted() {
        if (!window.klaviyo && this.$root.config.emailOptIn.klaviyoAPIKey) {
            !function () { if (!window.klaviyo) { window._klOnsite = window._klOnsite || []; try { window.klaviyo = new Proxy({}, { get: function (n, i) { return "push" === i ? function () { var n; (n = window._klOnsite).push.apply(n, arguments) } : function () { for (var n = arguments.length, o = new Array(n), w = 0; w < n; w++)o[w] = arguments[w]; var t = "function" == typeof o[o.length - 1] ? o.pop() : void 0, e = new Promise((function (n) { window._klOnsite.push([i].concat(o, [function (i) { t && t(i), n(i) }])) })); return e } } }) } catch (n) { window.klaviyo = window.klaviyo || [], window.klaviyo.push = function () { var n; (n = window._klOnsite).push.apply(n, arguments) } } } }();

            const script = document.createElement('script')

            script.type = 'text/javascript';
            script.async = true;
            script.src = `//static.klaviyo.com/onsite/js/${this.$root.config.emailOptIn.klaviyoAPIKey}/klaviyo.js`;

            document.body.appendChild(script);

            script.addEventListener('load', () => {
                this.klaviyoLoaded = true;
            });
        } else if (window.klaviyo) {
            // Assumed injected via Shopify App embed
            this.klaviyoLoaded = true;
        }

        this.$nextTick(() => {
            this.form = this.$el.querySelector('form');

            if (this.form) {
                this.form.querySelector('[name="return_to"]').value = `${window.location.pathname}${window.location.hash}/true/results`;

                this._submitControls = Array.from(
                this.form.querySelectorAll('button[type="submit"], input[type="submit"]')
                );

                this.form.addEventListener('submit', event => {
                    event.stopImmediatePropagation();
                    event.preventDefault();

                    // guard against double submits
                    if (this.isSubmitting) return;

                    this.lockSubmit(true);

                    sessionStorage.getItem('quiz_variant_skus');
                    JSON.parse(sessionStorage.getItem('quiz_variant_skus') || '[]');
                    location.origin;

                    if (this.klaviyoLoaded) {
                        this.pushToKlaviyo();
                    } else {
                        this.form.submit();
                    }
                });
            }
        });
    },
    methods: {
        lockSubmit(lock) {
            this.isSubmitting = !!lock;
            if (!this._submitControls) return;

            for (const el of this._submitControls) {
                if (lock) {
                    el.setAttribute('disabled', 'disabled');
                    el.setAttribute('aria-busy', 'true');
                    el.classList.add('btn--loading');
                    el.style.pointerEvents = 'none';
                } else {
                    el.removeAttribute('disabled');
                    el.removeAttribute('aria-busy');
                    el.classList.remove('btn--loading');
                    el.style.pointerEvents = '';
                }
            }

            // Optionally make the whole form inert to block Enter key on other inputs
            if (this.form) {
                if (lock) {
                    this.form.setAttribute('data-locked', 'true');
                } else {
                    this.form.removeAttribute('data-locked');
                }
            }
        },
        goToNext() {
            this.$router.push({
                name: 'Results',
                params: {
                    age: this.value,
                    emailOptIn: false
                }
            });
        },
        async resolveVariantSkusFromServer() {
            try {
                // 1) Fetch all pages of the collection JSON
                let currentPage = 1;
                let totalPages = 1;
                const productsPerPage = 50; // keep in sync with Results.vue
                let allProducts = [];
                let totalProducts = 0;

                while (currentPage <= totalPages) {
                    const res = await fetch(`/collections/quiz-products?sort_by=title-ascending&view=json&page=${currentPage}`, { priority: 'high' });
                    if (!res.ok) break;
                    const data = await res.json();
                    allProducts = allProducts.concat(data.products || []);
                    totalProducts = data.totalProducts || allProducts.length;
                    totalPages = Math.ceil(totalProducts / (productsPerPage + 1));
                    currentPage++;
                }

                // 2) Apply the exact same filters as Results.vue
                const p = this.$route.params;
                let filtered = allProducts
                    .filter(pr => pr.quiz_sleep?.some(x => x.option === p.sleep))
                    .filter(pr => pr.quiz_stress?.some(x => x.option === p.stress))
                    .filter(pr => pr.quiz_live?.some(x => x.option === p.live))
                    .filter(pr => pr.quiz_diet?.some(x => (p.diet || '').split(',').includes(x.option)));

                const primary = (p.beauty || '').split(',')[0];
                filtered = filtered.filter(pr => pr.quiz_beauty?.some(x => x.option === primary));

                if ((p.beauty || '').split(',').length > 1) {
                    const secondary = p.beauty.split(',')[1];
                    if (primary === 'skin') filtered = filtered.filter(pr => pr.quiz_beauty_skin?.some(x => x.option === secondary));
                    if (primary === 'hair') filtered = filtered.filter(pr => pr.quiz_beauty_hair?.some(x => x.option === secondary));
                    if (primary === 'nails') filtered = filtered.filter(pr => pr.quiz_beauty_nails?.some(x => x.option === secondary));
                }

                filtered = filtered
                    .filter(pr => pr.quiz_supplements?.some(x => x.option === p.supplements))
                    .filter(pr => pr.quiz_age?.some(x => x.option === p.age));

                // 3) Extract/resolve SKUs
                const skuPromises = filtered.map(async (pr) => {
                    if (pr?.variantSku) return String(pr.variantSku).trim();
                    if (pr?.sku) return String(pr.sku).trim();

                    // fetch product detail and match the variant by varientId
                    if (pr?.handle && pr?.varientId) {
                        const r = await fetch(`/products/${pr.handle}.js`, { priority: 'low' });
                        if (!r.ok) return null;
                        const prod = await r.json();
                        const variantIdNum = Number(pr.varientId);
                        const match = Array.isArray(prod?.variants)
                            ? prod.variants.find(v => Number(v?.id) === variantIdNum)
                            : null;
                        return match?.sku ? String(match.sku).trim() : null;
                    }
                    return null;
                });

                const skusRaw = await Promise.all(skuPromises);
                return Array.from(new Set(skusRaw.filter(Boolean)));
            } catch {
                return [];
            }
        },
        async pushToKlaviyo() {
            const formData = new FormData(this.form);

            // Rebuild the SKU list directly (no sessionStorage)
            const productVariantSkus = await this.resolveVariantSkusFromServer();

            const [beautyInterest, beautyInterestSecondary] = (this.$route.params.beauty || '').split(',');

            try {
                await window.klaviyo.identify({
                    email: formData.get('contact[email]'),
                    first_name: formData.get('contact[first_name]')
                });

                await window.klaviyo.track('Quiz Results', {
                    sleep: this.$route.params.sleep,
                    stress: this.$route.params.stress,
                    location: this.$route.params.live,
                    diet: this.$route.params.diet,
                    beauty_interest: beautyInterest,
                    ...(beautyInterest === 'skin' && { skin_improvement: beautyInterestSecondary }),
                    ...(beautyInterest === 'nails' && { nail_improvement: beautyInterestSecondary }),
                    ...(beautyInterest === 'hair' && { hair_improvement: beautyInterestSecondary }),
                    supplements: this.$route.params.supplements,
                    age: this.$route.params.age,

                    // send both forms for reliability/segmentation
                    variant_skus: productVariantSkus,                 // API-friendly
                    variant_skus_csv: productVariantSkus.join(',')    // UI/segment-friendly
                });

                // give the request a tick to flush before navigation
                await new Promise(r => setTimeout(r, 150));
            } catch (_) {
                // don't block UX on analytics failures
            } finally {
                this.form.submit();
            }
        }
    }
}
</script>
